import type {
  Challenges,
  Credentials,
} from '@jotta/grpc-connect-openapi/esm/openapi/credentials_pb'
import { Challenge } from '@jotta/grpc-connect-openapi/esm/openapi/credentials_pb'
import type { PartialMessage } from '@bufbuild/protobuf'
import { VerifyOtpDialog } from '../VerifyOtpDialog/VerifyOtpDialog'
import { VerifyPasswordDialog } from '../VerifyPasswordDialog/VerifyPasswordDialog'
import { VerifyPhoneDialog } from '../VerifyPhoneDialog/VerifyPhoneDialog'
import { VerifyEmailOtpDialog } from '../VerifyEmailOtpDialog/VerifyEmailOtpDialog'
import { t } from '@lingui/macro'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'
import { useCallback, useEffect, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { RadixModal } from '../RadixModal/RadixModal'
import { RadixDialog } from '../RadixDialog/RadixDialog'
import * as Dialog from '@radix-ui/react-dialog'
import Spinner from '../../Elements/Spinner/Spinner'

export type ChallengeDialogProps = {
  challenges: Challenges | undefined
  requestType: 'positive' | 'destructive'
  onRequest: (credentials: PartialMessage<Credentials>) => Promise<any>
  onClose: () => void
  skipCloseButton?: boolean
  defaultCredentials?: PartialMessage<Credentials>
}

function findErrorMessage(
  challenges: Challenges | undefined,
  currentCredentials: PartialMessage<Credentials>,
) {
  const challenge = challenges?.challenges.find(_ => true)
  switch (challenge) {
    case undefined:
      return undefined
    case Challenge.PASSWORD:
      return currentCredentials.password
        ? t`The password was wrong!`
        : undefined
    case Challenge.TOTP:
      return currentCredentials.totpCode
        ? t`Invalid authentication code, please make sure the code is typed in correctly.`
        : undefined
    case Challenge.EMAIL_OTP:
      return currentCredentials.emailCode
        ? t`Invalid authentication code, please make sure the code is typed in correctly.`
        : undefined
    case Challenge.SMS_OTP:
      return currentCredentials.smsCode
        ? t`Invalid authentication code, please make sure the code is typed in correctly.`
        : undefined
    case Challenge.UNKNOWN_CHALLENGE:
      throw new Error('unrecognized challenge')
    default:
      return exhaustiveGuard(challenge)
  }
}

function ChallengeDialogContent({
  challenges,
  requestType,
  onRequest,
  onClose,
  skipCloseButton,
  defaultCredentials,
}: ChallengeDialogProps) {
  const [currentChallenge, setCurrentChallenge] = useState<Challenge>()
  const nextChallenge = challenges?.challenges?.find(_ => true)
  useEffect(() => {
    nextChallenge && setCurrentChallenge(nextChallenge)
  }, [setCurrentChallenge, nextChallenge])

  const [currentCredentials, setCurrentCredentials] = useState<
    PartialMessage<Credentials>
  >(defaultCredentials || {})

  const {
    mutate,
    error: mutationError,
    isPending,
  } = useMutation({
    mutationFn: onRequest,
    throwOnError: false,
  })

  const error =
    mutationError || findErrorMessage(challenges, currentCredentials)

  const sendCredentials = useCallback(
    (credentials: PartialMessage<Credentials>) => {
      const nextCredentials = {
        ...currentCredentials,
        ...credentials,
      }
      setCurrentCredentials(nextCredentials)
    },
    [currentCredentials, setCurrentCredentials],
  )

  useEffect(() => {
    mutate(currentCredentials)
  }, [mutate, currentCredentials])

  const destructive = requestType === 'destructive'

  switch (currentChallenge) {
    case Challenge.PASSWORD:
      return (
        <VerifyPasswordDialog
          title={t({ id: 'Authentication Required' })}
          cancelButtonText={t({ id: 'Cancel' })}
          verifyButtonText={t({ id: 'Continue' })}
          destructive={destructive}
          onVerify={password =>
            sendCredentials({
              password,
            })
          }
          onClose={onClose}
          skipCloseButton={skipCloseButton}
          isLoading={isPending}
          errorMessage={error}
        />
      )
    case Challenge.TOTP:
      return (
        <VerifyOtpDialog
          title={t({ id: 'Authentication Required' })}
          destructive={destructive}
          onVerify={totpCode =>
            sendCredentials({
              totpCode,
            })
          }
          onClose={onClose}
          skipCloseButton={skipCloseButton}
          isLoading={isPending}
          errorMessage={error}
        />
      )
    case Challenge.EMAIL_OTP:
      return (
        <VerifyEmailOtpDialog
          title={t({ id: 'Authentication Required' })}
          destructive={destructive}
          onVerify={(emailCode: string) =>
            sendCredentials({
              emailCode,
            })
          }
          onClose={onClose}
          skipCloseButton={skipCloseButton}
          isLoading={isPending}
          errorMessage={error}
        />
      )
    case Challenge.SMS_OTP:
      return (
        <VerifyPhoneDialog
          onClose={onClose}
          skipCloseButton={skipCloseButton}
          errorMessage={error}
          destructive={destructive}
          uiState={isPending ? 'VERIFY_CODE_PENDING' : 'VERIFY_CODE'}
          title={t`Type in verification code`}
          verifyButtonText={t`Continue`}
          cancelButtonText={t`Cancel`}
          onVerify={(_, smsCode) =>
            sendCredentials({
              smsCode,
            })
          }
        />
      )
    case Challenge.UNKNOWN_CHALLENGE:
    case undefined:
      return (
        <RadixDialog onClose={onClose} errorMessage={error}>
          <div className="my-10 h-20">
            {isPending && <Spinner className="mx-auto" />}
          </div>
        </RadixDialog>
      )
    default:
      return exhaustiveGuard(currentChallenge)
  }
}

export function ChallengeDialog(props: ChallengeDialogProps) {
  return (
    <Dialog.Root open={true}>
      <ChallengeDialogContent {...props} skipCloseButton />
    </Dialog.Root>
  )
}

export function ChallengeDialogModal(props: ChallengeDialogProps) {
  return (
    <RadixModal open={true} onClose={props.onClose}>
      <ChallengeDialogContent {...props} />
    </RadixModal>
  )
}
